/**
 * React Query hooks for badges
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@lib/api';
import { useAuth } from '@lib/auth';
import type { BadgesResponse, RecentBadge } from '@lib/types';

// Query keys
export const badgeKeys = {
  all: ['badges'] as const,
  list: () => [...badgeKeys.all, 'list'] as const,
  recent: (limit?: number) => [...badgeKeys.all, 'recent', limit] as const,
  user: (userId: string) => [...badgeKeys.all, 'user', userId] as const,
};

// Fetch all badges with user's earned status
export function useBadges() {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: badgeKeys.list(),
    queryFn: () => api.get<BadgesResponse>('/api/badges'),
    staleTime: 1000 * 60, // 1 minute
    enabled: isAuthenticated && !isLoading, // Only fetch when authenticated
  });
}

// Fetch recently earned badges (for activity feed)
export function useRecentBadges(limit: number = 10) {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: badgeKeys.recent(limit),
    queryFn: () => api.get<{ badges: RecentBadge[] }>(`/api/badges/recent?limit=${limit}`),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refetch every minute
    enabled: isAuthenticated && !isLoading, // Only fetch when authenticated
  });
}

// Fetch badges for a specific user
export function useUserBadges(userId: string) {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: badgeKeys.user(userId),
    queryFn: () => api.get<{ badges: Array<{
      id: string;
      name: string;
      description: string;
      icon: string;
      category: string;
      bonusPoints: number;
      earnedAt: string;
    }> }>(`/api/badges/user/${userId}`),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated && !isLoading && !!userId, // Only fetch when authenticated and userId is provided
  });
}
